function deepClone(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

function resolvePluginContainer(routes, match) {
  if (!Array.isArray(routes)) return null;
  const m = match || { is_plugin: 1 };
  for (let i = 0; i < routes.length; i++) {
    const r = routes[i];
    if (m.path != null && r.path === m.path) return r;
    if (m.router_name != null && r.router_name === m.router_name) return r;
    if (m.vue_name != null && r.vue_name === m.vue_name) return r;
    if (m.is_plugin != null && r.is_plugin == m.is_plugin) return r;
  }
  return null;
}

function pushUniqueChildren(target, children) {
  if (!Array.isArray(target._child)) target._child = [];
  const keys = target._child.map((c) => (c.id != null ? c.id : c.path));
  (children || []).forEach((c) => {
    const k = c.id != null ? c.id : c.path;
    if (keys.indexOf(k) === -1) {
      target._child.push(c);
      keys.push(k);
    }
  });
}

function composeRoutes(baseRoutes, overlay) {
  const tree = deepClone(baseRoutes) || [];
  if (!overlay) return tree;
  const container = resolvePluginContainer(tree, overlay.match);
  if (!container) return tree;
  container.component = "Layout";
  pushUniqueChildren(container, deepClone(overlay.children || []));
  return tree;
}

function firstChildPath(children) {
  if (!Array.isArray(children) || !children.length) return null;
  let first = children[0];
  if (!first.path) {
    const nested = first._child || first.children || [];
    if (!nested.length) return null;
    first = nested[0];
  }
  return first.path ? { path: first.path, props: first.props } : null;
}

function createRouteNameState() {
  return { used: Object.create(null), counts: Object.create(null) };
}

function routeNameSuffix(path, fallback) {
  const suffix = String(path || "")
    .replace(/^\/+/, "")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return suffix || String(fallback);
}

function uniqueRouteName(name, path, state) {
  if (name == null || name === "") return name;
  const key = String(name);
  if (!state.used[key]) {
    state.used[key] = true;
    state.counts[key] = 1;
    return name;
  }

  const nextCount = (state.counts[key] || 1) + 1;
  state.counts[key] = nextCount;
  const base = key + "__" + routeNameSuffix(path, nextCount);
  let candidate = base;
  let collision = 2;
  while (state.used[candidate]) {
    candidate = base + "_" + collision;
    collision += 1;
  }
  state.used[candidate] = true;
  return candidate;
}

function shouldRegisterRouteNode(item) {
  return item && (item.app_level == 0 || item.app_level == 2);
}

function assembleMenuTree(routes, level, nameState) {
  const lv = level || 1;
  const state = nameState || createRouteNameState();
  const tree = [];
  const plugin = [];
  (routes || []).forEach((item) => {
    const node = {
      path: item.path,
      name: uniqueRouteName(item.vue_name, item.path, state),
      componentRef: item.component,
      meta: {
        title: item.name,
        layout: item.layout,
        router_name: item.router_name,
        icon: item.icon,
        notright: item.notright,
        keep: item.keep_alive,
        level: lv,
      },
    };
    if (item.props) node.meta.props = item.props;
    if (item._child && item._child.length > 0) {
      node.children = assembleMenuTree(item._child, lv + 1, state).tree;
    }
    if (shouldRegisterRouteNode(item)) tree.push(node);
    else plugin.push(node);
  });
  return { tree, plugin };
}

function resolveBootstrapDecision(opts) {
  const options = opts || {};
  if (options.isWhitelisted) {
    return { shouldBootstrap: false, bootstrappedPath: null };
  }
  if (options.isApplyingRoutes) {
    return { shouldBootstrap: false, bootstrappedPath: options.bootstrappedPath || null };
  }
  const needBootstrap = options.routeadds == 0 || options.matchedLength === 0 || options.shouldResetOverlay;
  if (needBootstrap && options.bootstrappedPath !== options.fullPath) {
    return { shouldBootstrap: true, bootstrappedPath: options.fullPath };
  }
  return { shouldBootstrap: false, bootstrappedPath: null };
}

function isLayoutMenuItem(item) {
  return item && item.meta && item.meta.layout == 1;
}

function shouldLeavePluginForMenuClick(item, currentPath) {
  if (!item) return false;
  if (item.path) return item.path !== currentPath;

  const children = item.children || [];
  if (!children.length || children[0].path === currentPath) return false;

  for (let i = 0; i < children.length; i += 1) {
    const child = children[i];
    if (!isLayoutMenuItem(child)) continue;

    if (child.children && child.children.length) {
      const first = child.children[0];
      return Boolean(first && first.path && first.path !== currentPath);
    }

    return Boolean(child.path && child.path !== currentPath);
  }

  return false;
}

function safeParse(raw, fallback) {
  if (raw == null) return fallback;
  try { return JSON.parse(raw); } catch (e) { return fallback; }
}

function readBaseFrom(storage) {
  return safeParse(storage.getItem("routes"), []);
}

// overlay 持久化:复用既有键 pluginid / pluginroute(存 children) / is_plugin / pluginmatch
function writeOverlayTo(storage, overlay) {
  if (!overlay) return clearOverlayIn(storage);
  storage.setItem("is_plugin", "1");
  storage.setItem("pluginid", String(overlay.pluginId));
  storage.setItem("pluginroute", JSON.stringify(overlay.children || []));
  storage.setItem("pluginmatch", JSON.stringify(overlay.match || { is_plugin: 1 }));
}

function readOverlayFrom(storage) {
  if (storage.getItem("is_plugin") == null) return null;
  const children = safeParse(storage.getItem("pluginroute"), null);
  if (!Array.isArray(children)) return null;
  return {
    pluginId: storage.getItem("pluginid"),
    children: children,
    match: safeParse(storage.getItem("pluginmatch"), { is_plugin: 1 }),
  };
}

function clearOverlayIn(storage) {
  storage.removeItem("is_plugin");
  storage.removeItem("pluginid");
  storage.removeItem("pluginroute");
  storage.removeItem("pluginmatch");
  storage.removeItem("pluginskipreconstruct");
  storage.removeItem("routesOld");
}

// —— 深链/落地页判定(纯;port 自 pluginRouteCache.buildPluginRouteForPath,改为不变异)——
function isLockedRoute(route) {
  const access = route && (route.access || route.plugin_access || (route.plugin && (route.plugin.access || route.plugin.plugin_access)));
  return Boolean(access && typeof access === "object" && access.can_use === false);
}

function subtreeHasPath(node, path, seen) {
  if (!node || isLockedRoute(node)) return false;
  const key = node.id != null ? node.id : node.path;
  const s = seen || [];
  if (key && s.indexOf(key) !== -1) return false;
  if (node.path === path) return true;
  const ns = key ? s.concat(key) : s;
  return (node._child || []).some((c) => subtreeHasPath(c, path, ns));
}

// to.path 是否落在当前 overlay 的插件子树内 -> 决定刷新/导航时是否保留 overlay
function isOverlayPath(overlay, path) {
  if (!overlay || !Array.isArray(overlay.children) || !path) return false;
  return overlay.children.some((c) => subtreeHasPath(c, path));
}

function shouldResetOverlayForPath(overlay, path) {
  return Boolean(overlay && path && !isOverlayPath(overlay, path));
}

function shouldResetOverlayForNavigation(overlay, toPath, fromPath) {
  if (toPath === fromPath) return false;
  return shouldResetOverlayForPath(overlay, toPath);
}

const WEISHOP_SCOPES = ["/weishop/shop", "/weishop/merchants"];

function scopedWeishopPrefix(path) {
  if (!path) return "";
  for (let i = 0; i < WEISHOP_SCOPES.length; i += 1) {
    const prefix = WEISHOP_SCOPES[i];
    if (path === prefix || path.indexOf(prefix + "/") === 0) return prefix;
  }
  return "";
}

function scopedWeishopCandidate(scope, targetPath) {
  if (!scope || !targetPath || targetPath.indexOf("/weishop/") !== 0) return null;
  if (targetPath === scope || targetPath.indexOf(scope + "/") === 0) return null;

  for (let i = 0; i < WEISHOP_SCOPES.length; i += 1) {
    const knownScope = WEISHOP_SCOPES[i];
    if (targetPath === knownScope || targetPath.indexOf(knownScope + "/") === 0) {
      return scope + targetPath.slice(knownScope.length);
    }
  }

  return scope + targetPath.slice("/weishop".length);
}

function resolveScopedWeishopPath(baseRoutes, overlay, fromPath, toPath) {
  const scope = scopedWeishopPrefix(fromPath);
  const candidate = scopedWeishopCandidate(scope, toPath);
  if (!candidate || candidate === toPath) return null;
  return subtreeHasPath({ _child: composeRoutes(baseRoutes, overlay) }, candidate) ? candidate : null;
}

// 无持久 overlay 时的深链重建:在 base 的 is_plugin 容器内找到包含 path 的插件,返回 overlay 描述(不变异 base)
function reconstructOverlay(baseRoutes, path) {
  if (!Array.isArray(baseRoutes) || !path) return null;
  for (let i = 0; i < baseRoutes.length; i++) {
    const route = baseRoutes[i];
    if (route.is_plugin != 1 || !Array.isArray(route._child)) continue;
    const plugin = route._child.find((child) => subtreeHasPath(child, path));
    if (!plugin || isLockedRoute(plugin) || !Array.isArray(plugin._child) || plugin._child.length === 0) continue;
    return { pluginId: plugin.id, children: plugin._child, match: { path: route.path } };
  }
  return null;
}

module.exports = {
  resolvePluginContainer, composeRoutes, firstChildPath, assembleMenuTree,
  readBaseFrom, readOverlayFrom, writeOverlayTo, clearOverlayIn,
  isOverlayPath, reconstructOverlay, resolveBootstrapDecision,
  shouldLeavePluginForMenuClick, shouldResetOverlayForPath, shouldResetOverlayForNavigation,
  resolveScopedWeishopPath,
};
